import { useState, useCallback, useEffect, useMemo } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTimeAgo } from "@/hooks/use-time-ago";
import {
  Shield,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  XCircle,
  Ban,
  Search,
  X,
  ShieldAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAlerts, useBulkUpdateAlerts, useAlertFeedback, useUsers } from "@/hooks/use-api";
import { AlertTable } from "@/components/alerts/alert-table";
import { AlertDetailPanel } from "@/components/alerts/alert-detail-panel";
import { cn } from "@/lib/utils";
import { APP_BASE, CLASSIC_APP_BASE } from "@/lib/app-routes";
import type { AlertSeverity, AlertStatus } from "@/types/api";

const PAGE_LIMIT = 25;

type SeverityFilter = AlertSeverity | "all";
type StatusFilter = AlertStatus | "all";
type OwnerFilter = "all" | "me" | "unassigned";

function severityFilterFrom(value: string | null): SeverityFilter {
  return value === "critical" || value === "high" || value === "medium" || value === "low"
    ? value
    : "all";
}

function statusFilterFrom(value: string | null): StatusFilter {
  return value === "new" || value === "acknowledged" || value === "investigating" || value === "resolved"
    ? value
    : "all";
}

function ownerFilterFrom(value: string | null): OwnerFilter {
  return value === "me" || value === "unassigned" ? value : "all";
}

export default function AlertsPage() {
  const { alertId: routeAlertId } = useParams<{ alertId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isClassic = location.pathname.startsWith(CLASSIC_APP_BASE);
  const [searchParams, setSearchParams] = useSearchParams();
  const severityFilter = severityFilterFrom(searchParams.get("severity"));
  const statusFilter = statusFilterFrom(searchParams.get("status"));
  const ownerFilter = ownerFilterFrom(searchParams.get("owner"));
  const searchFilter = searchParams.get("q")?.trim() ?? "";
  const [searchInput, setSearchInput] = useState(searchFilter);
  const [offset, setOffset] = useState(0);
  const [selectedAlertIds, setSelectedAlertIds] = useState<Set<string>>(new Set());
  const [selectedAlertId, setSelectedAlertId] = useState<string | null>(
    routeAlertId ?? null,
  );
  const [bulkError, setBulkError] = useState<string | null>(null);

  const queryParams = {
    severity: severityFilter === "all" ? undefined : severityFilter,
    status: statusFilter === "all" ? undefined : statusFilter,
    owner: ownerFilter === "all" ? undefined : ownerFilter,
    search: searchFilter || undefined,
    limit: PAGE_LIMIT,
    offset,
  };

  const qc = useQueryClient();
  const { data, isLoading, isError, refetch, dataUpdatedAt } = useAlerts(queryParams);
  const { data: usersData } = useUsers();
  const updatedAgo = useTimeAgo(dataUpdatedAt);
  const bulkUpdate = useBulkUpdateAlerts();
  const feedbackMutation = useAlertFeedback();

  useEffect(() => {
    setSelectedAlertId(routeAlertId ?? null);
  }, [routeAlertId]);

  useEffect(() => {
    setSearchInput(searchFilter);
  }, [searchFilter]);

  useEffect(() => {
    setOffset(0);
    setSelectedAlertIds(new Set());
  }, [severityFilter, statusFilter, ownerFilter, searchFilter]);

  const alerts = data?.alerts ?? [];
  const total = data?.total ?? 0;
  const ownerCounts = data?.owner_counts ?? { all: 0, mine: 0, unassigned: 0 };
  const assigneeNames = useMemo(
    () => Object.fromEntries(
      (usersData?.users ?? []).map((tenantUser) => [
        tenantUser.id,
        tenantUser.name || tenantUser.email,
      ]),
    ),
    [usersData?.users],
  );
  const hasNext = offset + alerts.length < total;
  const hasPrev = offset > 0;

  useEffect(() => {
    if (isClassic || !isDesktop || routeAlertId || alerts.length === 0) return;
    navigate(
      `${APP_BASE}/alerts/${encodeURIComponent(alerts[0].id)}${location.search}`,
      { replace: true },
    );
  }, [alerts, isClassic, isDesktop, location.search, navigate, routeAlertId]);

  useEffect(() => {
    if (isClassic || !isDesktop || !selectedAlertId) return;
    function handleQueueShortcut(event: KeyboardEvent) {
      if (event.key.toLowerCase() !== "j" && event.key.toLowerCase() !== "k") return;
      const target = event.target;
      if (target instanceof Element && target.closest("input, textarea, select, button, a")) return;
      const currentIndex = alerts.findIndex((alert) => alert.id === selectedAlertId);
      if (currentIndex < 0) return;
      const direction = event.key.toLowerCase() === "j" ? 1 : -1;
      const nextIndex = Math.min(Math.max(currentIndex + direction, 0), alerts.length - 1);
      if (nextIndex === currentIndex) return;
      event.preventDefault();
      handleRowClick(alerts[nextIndex].id);
    }
    document.addEventListener("keydown", handleQueueShortcut);
    return () => document.removeEventListener("keydown", handleQueueShortcut);
  }, [alerts, isClassic, isDesktop, selectedAlertId]);

  function handleSeverityChange(value: SeverityFilter) {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("severity");
    else next.set("severity", value);
    setSearchParams(next, { replace: true });
  }

  function handleStatusChange(value: StatusFilter) {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("status");
    else next.set("status", value);
    setSearchParams(next, { replace: true });
  }

  function handleOwnerChange(value: OwnerFilter) {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("owner");
    else next.set("owner", value);
    setSearchParams(next, { replace: true });
  }

  function handleSearchSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const next = new URLSearchParams(searchParams);
    const value = searchInput.trim();
    if (value) next.set("q", value);
    else next.delete("q");
    setSearchParams(next, { replace: true });
  }

  function handleSearchClear() {
    setSearchInput("");
    const next = new URLSearchParams(searchParams);
    next.delete("q");
    setSearchParams(next, { replace: true });
  }

  const toggleSelect = useCallback((id: string) => {
    setSelectedAlertIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    setSelectedAlertIds((prev) => {
      const allSelected = alerts.length > 0 && alerts.every((a) => prev.has(a.id));
      if (allSelected) {
        return new Set();
      }
      return new Set(alerts.map((a) => a.id));
    });
  }, [alerts]);

  function handleBulkAction(status: AlertStatus) {
    const ids = Array.from(selectedAlertIds);
    if (ids.length === 0) return;
    setBulkError(null);
    bulkUpdate.mutate(
      { alertIds: ids, status },
      {
        onSuccess: () => setSelectedAlertIds(new Set()),
        onError: (err) => setBulkError(err.message),
      },
    );
  }

  function handleRowClick(alertId: string) {
    setSelectedAlertId(alertId);
    const basePath = location.pathname.startsWith(CLASSIC_APP_BASE)
      ? CLASSIC_APP_BASE
      : APP_BASE;
    navigate(`${basePath}/alerts/${encodeURIComponent(alertId)}${location.search}`);
  }

  function handleDetailClose() {
    setSelectedAlertId(null);
    const basePath = location.pathname.startsWith(CLASSIC_APP_BASE)
      ? CLASSIC_APP_BASE
      : APP_BASE;
    navigate(`${basePath}/alerts${location.search}`);
  }

  function handlePageChange(nextOffset: number) {
    setOffset(nextOffset);
    setSelectedAlertId(null);
    if (!isClassic) {
      navigate(`${APP_BASE}/alerts${location.search}`, { replace: true });
    }
  }

  async function handleBulkFalsePositive() {
    const ids = Array.from(selectedAlertIds);
    if (ids.length === 0) return;
    setBulkError(null);

    // Batch all mutations, then invalidate once at the end
    const results = await Promise.allSettled(
      ids.map((id) =>
        feedbackMutation.mutateAsync(
          { alertId: id, feedback: "false_positive" },
          // Skip per-mutation invalidation — we'll do it once after all complete
          { onSuccess: undefined },
        ),
      ),
    );

    // Single invalidation after all mutations
    qc.invalidateQueries({ queryKey: ["alerts"] });
    qc.invalidateQueries({ queryKey: ["alert"] });

    const failures = results.filter((r) => r.status === "rejected");
    if (failures.length > 0) {
      setBulkError(`${failures.length} of ${ids.length} failed to mark as false positive`);
    }
    setSelectedAlertIds(new Set());
  }

  const queueContent = (
    <div className={cn("space-y-4", !isClassic && "p-4")}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Alerts</h1>
            <p className="text-sm text-muted-foreground">
              {alerts.length > 0
                ? `Showing ${offset + 1}-${offset + alerts.length} of ${total}`
                : "No alerts"}
            </p>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <form onSubmit={handleSearchSubmit} role="search" className="flex items-center gap-2">
          <label className="relative" htmlFor="alert-search">
            <span className="sr-only">Search alerts</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              id="alert-search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search alerts"
              maxLength={200}
              className="h-9 w-56 rounded-md border border-input bg-background pl-9 pr-8 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            {searchInput && (
              <button
                type="button"
                aria-label="Clear alert search"
                onClick={handleSearchClear}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm p-1 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </label>
          <Button type="submit" variant="outline" size="sm">Search</Button>
        </form>

        <select
          value={severityFilter}
          onChange={(e) => handleSeverityChange(e.target.value as SeverityFilter)}
          aria-label="Filter by severity"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">All Severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>

        <select
          value={ownerFilter}
          onChange={(e) => handleOwnerChange(e.target.value as OwnerFilter)}
          aria-label="Filter by owner"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">All Owners ({ownerCounts.all})</option>
          <option value="me">Mine ({ownerCounts.mine})</option>
          <option value="unassigned">Unassigned ({ownerCounts.unassigned})</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => handleStatusChange(e.target.value as StatusFilter)}
          aria-label="Filter by status"
          className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">All Statuses</option>
          <option value="new">New</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="investigating">Investigating</option>
          <option value="resolved">Resolved</option>
        </select>

        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          <RefreshCw className={cn("mr-1.5 h-3.5 w-3.5", isLoading && "animate-spin")} />
          Refresh
        </Button>
        {updatedAgo && (
          <span className="text-xs text-muted-foreground">Updated {updatedAgo}</span>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedAlertIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/50 px-4 py-2">
          <span className="text-sm font-medium">
            {selectedAlertIds.size} selected
          </span>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkAction("acknowledged")}
              disabled={bulkUpdate.isPending}
            >
              <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
              Acknowledge
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkAction("resolved")}
              disabled={bulkUpdate.isPending}
            >
              <XCircle className="mr-1.5 h-3.5 w-3.5" />
              Resolve
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkFalsePositive}
              disabled={feedbackMutation.isPending}
            >
              <Ban className="mr-1.5 h-3.5 w-3.5" />
              Mark False Positive
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedAlertIds(new Set())}
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Error states */}
      {isError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          Failed to load alerts. Data will retry automatically.
        </div>
      )}
      {bulkError && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400">
          {bulkError}
        </div>
      )}

      {/* Alert table */}
      <AlertTable
        alerts={alerts}
        selectedIds={selectedAlertIds}
        onToggleSelect={toggleSelect}
        onSelectAll={selectAll}
        onRowClick={handleRowClick}
        isLoading={isLoading}
        assigneeNames={assigneeNames}
        variant={isClassic ? "table" : "queue"}
        activeId={selectedAlertId}
      />

      {/* Pagination */}
      {!isLoading && alerts.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing {offset + 1}-{offset + alerts.length} of {total}
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(Math.max(0, offset - PAGE_LIMIT))}
              disabled={!hasPrev}
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(offset + PAGE_LIMIT)}
              disabled={!hasNext}
            >
              Next
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

    </div>
  );

  if (isClassic) {
    return (
      <>
        {queueContent}
        <AlertDetailPanel alertId={selectedAlertId} onClose={handleDetailClose} />
      </>
    );
  }

  return (
    <div
      className="grid h-[calc(100dvh-4rem)] min-h-0 lg:grid-cols-[minmax(390px,42%)_minmax(0,1fr)]"
      data-testid="alert-triage-workspace"
    >
      <section
        aria-label="Alert queue"
        className={cn(
          "min-h-0 overflow-y-auto border-r border-border bg-[hsl(var(--app-canvas))] pb-[68px] lg:block lg:pb-0",
          selectedAlertId ? "hidden" : "block",
        )}
      >
        {queueContent}
      </section>
      <section
        aria-label="Selected alert"
        className={cn("min-h-0 bg-background pb-[68px] lg:block lg:pb-0", selectedAlertId ? "block" : "hidden")}
      >
        {selectedAlertId ? (
          <AlertDetailPanel
            alertId={selectedAlertId}
            onClose={handleDetailClose}
            presentation="workspace"
          />
        ) : (
          <div className="flex h-full items-center justify-center p-8 text-center">
            <div>
              <ShieldAlert className="mx-auto h-9 w-9 text-muted-foreground" />
              <h2 className="mt-4 font-semibold">Select an alert</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Choose an alert from the queue to begin triage.
              </p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(query).matches
      : false,
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);

  return matches;
}
